<template>
  <nav class="MyMapHeader">
    <div class="MyMapHeader__block MyMapHeader__block--left">
      <router-link :to="{ name: 'home' }" class="Header__bannerLink">
        <img
          loading="lazy"
          src="@/assets/images/logo-sigcom.png"
          alt="Accueil"
          class="MyMapHeader__logo"
        />
      </router-link>
    </div>
    <CommuneSearch
      v-model="myMapStore.mapSearch"
      :disabled="isCommuneSelectionLocked"
      class="MyMapHeader__search"
      @commune-selected="handleCommuneSelect"
    />

    <div class="MyMapHeader__block MyMapHeader__block--left">
      <span class="MyMapHeader__tooltipHost MyMapHeader__resumeBtnWrap">
        <button
          type="button"
          class="MyMapHeader__shareBtn"
          @click="isResumeModalOpen = true"
        >
          <Icon icon="mdi:chart-box-outline" />
          {{ $t('resumeStatistique.button', 'Résumé statistique') }}
        </button>
      </span>

      <div class="MyMapHeader__shareMenu relative">
        <span class="MyMapHeader__tooltipHost">
          <button
            type="button"
            class="MyMapHeader__shareBtn MyMapHeader__shareBtn--partager"
            :class="{ 'MyMapHeader__shareBtn--open': isShareMenuOpen }"
            @click.stop="toggleShareMenu"
          >
            <svg
              class="MyMapHeader__shareIcon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M18,16.08C17.24,16.08 16.56,16.38 16.04,16.85L8.91,12.7C8.96,12.47 9,12.24 9,12C9,11.76 8.96,11.53 8.91,11.3L15.96,7.19C16.5,7.69 17.21,8 18,8A3,3 0 0,0 21,5A3,3 0 0,0 18,2A3,3 0 0,0 15,5C15,5.24 15.04,5.47 15.09,5.7L8.04,9.81C7.5,9.31 6.79,9 6,9A3,3 0 0,0 3,12A3,3 0 0,0 6,15C6.79,15 7.5,14.69 8.04,14.19L15.16,18.34C15.11,18.55 15.08,18.77 15.08,19C15.08,20.61 16.39,21.91 18,21.91C19.61,21.91 20.92,20.61 20.92,19A2.92,2.92 0 0,0 18,16.08Z"
              />
            </svg>
            {{ $t('myMap.header.share', 'Partager') }}
          </button>
          <span class="MyMapHeader__tooltipBubble" role="tooltip">
            {{ $t('myMap.header.shareTooltip') }}
          </span>
        </span>

        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <div v-if="isShareMenuOpen" class="MyMapHeader__shareDropdown">
            <div class="MyMapHeader__shareDropdownItem" @click="copyShareLink">
              <Icon :icon="isLinkCopied ? 'mdi:check' : 'mdi:link-variant'" />
              <span>{{
                isLinkCopied
                  ? $t('myMap.header.linkCopied', 'Lien copié !')
                  : $t('myMap.header.copyLink', 'Copier le lien')
              }}</span>
            </div>
          </div>
        </Transition>
      </div>

      <router-link
        v-if="userStore.userIsLogged"
        :to="{ name: RoutesKey.NATURAL_LANGUAGE_QUERY, query: gptLinkQuery }"
        class="MyMapHeader__gptBtn"
      >
        <Icon icon="heroicons:sparkles-20-solid" size="20" />
        <span>SIGCOM-GPT</span>
      </router-link>

      <div v-if="userStore.userIsLogged" class="MyMapHeader__userMenu relative">
        <button
          type="button"
          class="MyMapHeader__userBtn"
          :class="{ 'MyMapHeader__userBtn--open': isUserMenuOpen }"
          @click="toggleUserMenu"
        >
          <div class="MyMapHeader__userAvatar">
            <img
              v-if="currentUser?.logo"
              :src="baseUrl + currentUser.logo.contentUrl"
              :alt="currentUser?.userNom || 'User profile'"
              class="MyMapHeader__userAvatarImg"
            />
            <DefaultAvatar v-else sizeStyle="40" />
          </div>
          <div class="MyMapHeader__userText">
            <p class="MyMapHeader__userName">{{ currentUser?.userNom }}</p>
            <p class="MyMapHeader__userSub">{{ currentUser?.userPrenom }}</p>
          </div>
          <svg
            class="MyMapHeader__userChevron"
            :class="{ 'MyMapHeader__userChevron--open': isUserMenuOpen }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <div v-if="isUserMenuOpen" class="MyMapHeader__dropdown">
            <div
              class="MyMapHeader__dropdownItem"
              @click="(goToAccount(), (isUserMenuOpen = false))"
            >
              <Icon icon="gg:profile" color="color-black" />
              <span>{{ $t('header.account') }}</span>
            </div>
            <!-- Même entrée que dans l'en-tête général : le maire doit pouvoir revenir à son tableau de bord. -->
            <div
              v-if="userStore.userGouverneUneCommune"
              class="MyMapHeader__dropdownItem"
              @click="(goToDashboard(), (isUserMenuOpen = false))"
            >
              <Icon icon="heroicons-outline:chart-pie" color="black" />
              <span>{{ $t('header.dashboard') }}</span>
            </div>
            <div
              v-if="userStore.userPeutConsulterDashboardCentral"
              class="MyMapHeader__dropdownItem"
              @click="(goToDashboardCentral(), (isUserMenuOpen = false))"
            >
              <Icon icon="heroicons-outline:globe-alt" color="black" />
              <span>{{ $t('header.dashboardCentral') }}</span>
            </div>
            <div
              v-if="userStore.userAccedeAdministration()"
              class="MyMapHeader__dropdownItem"
              @click="(goToAdmin(), (isUserMenuOpen = false))"
            >
              <Icon icon="system-uicons:filtering" color="black" />
              <span>{{ $t('header.administration') }}</span>
            </div>
            <div
              class="MyMapHeader__dropdownItem"
              @click="(userStore.signOut(), (isUserMenuOpen = false))"
            >
              <Icon icon="material-symbols-light:logout-rounded" color="black" />
              <span>{{ $t('header.logout') }}</span>
            </div>
          </div>
        </Transition>
      </div>
      <router-link v-else class="MyMapHeader__loginLink" :to="{ name: RoutesKey.AUTH_SIGN_IN }">
        <Icon icon="heroicons-outline:arrow-right-on-rectangle" size="20" />
        <span>{{ $t('myMap.header.login') || 'Se connecter' }}</span>
      </router-link>
    </div>

    <ResumeStatistiqueModal
      :show="isResumeModalOpen"
      :commune="myMapStore.mapSearch"
      @close="isResumeModalOpen = false"
    />
  </nav>
</template>

<script setup lang="ts">
import CommuneSearch from '@/components/forms/CommuneSearch.vue'
import ResumeStatistiqueModal from '@/views/map/components/ResumeStatistiqueModal.vue'
import type { CommuneApiResponse } from '@/services/commune/CommuneService'
import { mapApiResponseToCommuneData } from '@/services/commune/CommuneService'
import Icon from '@/components/ui/Icon.vue'
import DefaultAvatar from '@/components/ui/DefaultAvatar.vue'
import { RoutesKey } from '@/models/enums/app/RoutesKey'
import { apiBaseUrl as baseUrl } from '@/config/env'
import { useMyMapStore } from '@/stores/myMapStore'
import { useUserStore } from '@/stores/userStore'
import {
  GEOPORTAIL_MODE_QUERY_KEY,
  GEOPORTAIL_COMMUNE_MODE_QUERY,
} from '@/services/commune/navigateGeoportailCommune'
import { useRoute, useRouter } from 'vue-router'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

const myMapStore = useMyMapStore()
const userStore = useUserStore()
const router = useRouter()
const route = useRoute()
const isUserMenuOpen = ref(false)
const isShareMenuOpen = ref(false)
const isLinkCopied = ref(false)
const isResumeModalOpen = ref(false)
const currentUser = computed(() => userStore.currentUser)
const isCommuneSelectionLocked = computed(() => myMapStore.lockedCommuneName != null)

/** Propagates the commune context to SIGCOM-GPT so it opens themed and scoped to the active commune. */
const gptLinkQuery = computed(() => {
  if (route.query[GEOPORTAIL_MODE_QUERY_KEY] !== GEOPORTAIL_COMMUNE_MODE_QUERY) return {}
  return {
    [GEOPORTAIL_MODE_QUERY_KEY]: GEOPORTAIL_COMMUNE_MODE_QUERY,
    commune: route.query.commune,
  }
})

watch(
  () => myMapStore.mapSearch,
  (mapSearch) => {
    const query = { ...route.query }
    if (isCommuneSelectionLocked.value) {
      const locked = myMapStore.lockedCommuneName
      if (locked) {
        query.commune = locked
        router.replace({ path: '/geoportail', query })
      }
      return
    }
    if (mapSearch?.name) {
      query.commune = mapSearch.name
    } else {
      delete query.commune
    }
    router.replace({ path: '/geoportail', query })
  },
  { immediate: false },
)

// CommuneSearch updates mapSearch via v-model, which triggers runApplyCommuneViewIfNeeded.
function handleCommuneSelect(commune: CommuneApiResponse): void {
  if (isCommuneSelectionLocked.value) return
  void myMapStore.zoomToCommuneBounds(
    commune.cmnTitre ?? '',
    commune.cmnLong,
    commune.cmnLat,
  )
  myMapStore.applyCommuneViewFromApiGeometry(commune)
  myMapStore.setCommuneFicheFromCommuneLayer(mapApiResponseToCommuneData(commune))
  if (commune.cmnCode) {
    myMapStore.selectCommuneFeatureByCode(commune.cmnCode)
  }
}

function toggleUserMenu(): void {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

function toggleShareMenu(): void {
  isShareMenuOpen.value = !isShareMenuOpen.value
}

function buildShareUrl(): string {
  const mapState = myMapStore.getSerializedMapState()
  const query: Record<string, string> = { mapState }
  if (myMapStore.mapSearch?.name) {
    query.commune = myMapStore.mapSearch.name
  }
  const resolved = router.resolve({ path: '/geoportail', query })
  return window.location.origin + resolved.href
}

async function copyShareLink(): Promise<void> {
  const url = buildShareUrl()
  try {
    await navigator.clipboard.writeText(url)
    isLinkCopied.value = true
    setTimeout(() => {
      isLinkCopied.value = false
      isShareMenuOpen.value = false
    }, 2000)
  } catch {
    console.error('Failed to copy share link to clipboard')
  }
}

function closeMenuOnClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (!target.closest('.MyMapHeader__userMenu')) {
    isUserMenuOpen.value = false
  }
  if (!target.closest('.MyMapHeader__shareMenu')) {
    isShareMenuOpen.value = false
  }
}

function goToAccount(): void {
  router.push({ name: RoutesKey.USERACCOUNT })
}

function goToAdmin(): void {
  router.push({ name: RoutesKey.ADMIN })
}

function goToDashboard(): void {
  router.push({ name: RoutesKey.DASHBOARD_MAIRE })
}

function goToDashboardCentral(): void {
  router.push({ name: RoutesKey.DASHBOARD_CENTRAL })
}

onMounted(() => {
  document.addEventListener('click', closeMenuOnClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenuOnClickOutside)
})
</script>

<style lang="scss">
.MyMapHeader {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 1.25rem;
  padding: 0.5rem 1.5rem;
  border-bottom: 1px solid var(--neutral-border);

  .MyMapHeader__block {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 1rem;
  }
  .MyMapHeader__logo {
    --dim-logo: 4.75rem;

    width: var(--dim-logo);
    height: var(--dim-logo);
    min-width: var(--dim-logo);
    max-width: var(--dim-logo);
    border-radius: var(--dim-radius);
  }
  .MyMapHeader__search {
    flex: 1 1 auto;

    ::placeholder {
      opacity: 1;
    }
  }

  .MyMapHeader__gptBtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-width: 10.25rem;
    padding: 0 1.25rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background-color: var(--color-main);
    color: white;
    font-weight: 500;
    font-size: 0.875rem;
    text-decoration: none;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--color-main-hover);
      color: white;
    }
  }

  .MyMapHeader__tooltipHost {
    position: relative;
    display: inline-flex;
    vertical-align: middle;
  }

  .MyMapHeader__tooltipBubble {
    position: absolute;
    left: 50%;
    top: calc(100% + 0.5rem);
    z-index: 200;
    transform: translateX(-50%);
    min-width: max-content;
    max-width: min(18rem, 92vw);
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.35;
    text-align: center;
    color: #fff;
    background-color: rgb(17 24 39);
    border-radius: 0.375rem;
    box-shadow:
      0 4px 6px -1px rgb(0 0 0 / 0.12),
      0 2px 4px -2px rgb(0 0 0 / 0.08);
    white-space: normal;
    pointer-events: none;
    visibility: hidden;
    opacity: 0;
    transition:
      opacity 0.12s ease,
      visibility 0.12s ease;
  }

  .MyMapHeader__tooltipHost:hover .MyMapHeader__tooltipBubble,
  .MyMapHeader__tooltipHost:focus-within .MyMapHeader__tooltipBubble {
    visibility: visible;
    opacity: 1;
  }

  .MyMapHeader__shareBtn {
    background-color: transparent;
    color: rgb(var(--v-theme-main-blue));
    border: 1px solid rgb(var(--v-theme-main-blue));
    min-width: 12rem;
    padding: 0 1.25rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: var(--dim-radius);
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
    cursor: pointer;
    height: 2.75rem;
    text-transform: none;
    font-size: 0.875rem;
    font-family: inherit;

    &:hover {
      background-color: rgba(var(--v-theme-main-blue), 0.08);
      color: rgb(var(--v-theme-main-blue));
    }
  }

  .MyMapHeader__shareMenu {
    position: relative;
  }

  .MyMapHeader__shareBtn--partager {
    min-width: auto;
    gap: 0.4rem;
  }

  .MyMapHeader__shareBtn--open {
    background-color: rgba(var(--v-theme-main-blue), 0.08);
  }

  .MyMapHeader__shareIcon {
    width: 1.125rem;
    height: 1.125rem;
    fill: currentColor;
    flex-shrink: 0;
  }

  .MyMapHeader__shareDropdown {
    position: absolute;
    right: 0;
    top: calc(100% + 0.375rem);
    min-width: 13rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid rgb(229 231 235);
    padding: 0.25rem 0;
    z-index: 50;
  }

  .MyMapHeader__shareDropdownItem {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: rgb(55 65 81);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgb(241 245 249);
    }
  }

  .MyMapHeader__userMenu {
    position: relative;
  }

  .MyMapHeader__userBtn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    background: transparent;
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;

    &:hover {
      background-color: rgb(241 245 249);
    }

    &--open {
      border-color: rgb(var(--v-theme-main-blue));
      background-color: rgb(255 255 255);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
  }

  .MyMapHeader__userAvatar {
    width: 2.5rem;
    height: 2.5rem;
    min-width: 2.5rem;
    border-radius: 9999px;
    overflow: hidden;
    border: 1px solid rgb(229 231 235);
  }

  .MyMapHeader__userAvatarImg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .MyMapHeader__userText {
    text-align: left;
  }

  .MyMapHeader__userName {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25;
    color: rgb(17 24 39);
  }

  .MyMapHeader__userSub {
    font-size: 0.75rem;
    line-height: 1.25;
    color: rgb(156 163 175);
  }

  .MyMapHeader__userChevron {
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  .MyMapHeader__dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 0.5rem;
    min-width: 12rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid rgb(229 231 235);
    padding: 0.25rem 0;
    z-index: 50;
  }

  .MyMapHeader__dropdownItem {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: rgb(55 65 81);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgb(241 245 249);
    }
  }

  .MyMapHeader__loginLink {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-width: 10.25rem;
    padding: 0 1.25rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background-color: var(--color-main);
    color: white;
    font-weight: 500;
    font-size: 0.875rem;
    text-decoration: none;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--color-main-hover);
      color: white;
    }
  }
}
</style>
